import { useState, useCallback, useEffect, useRef } from 'react';
import config from '../config';
import { useToast } from '@chakra-ui/react';

const useGoogleMapsLoader = () => {
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const geocoder = useRef(null);
  const toast = useToast();
  const initCallbacks = useRef([]);

  const initializeGoogleMaps = useCallback(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setGoogleMapsLoaded(true);
      geocoder.current = new window.google.maps.Geocoder();
      
      // Process any queued callbacks
      while (initCallbacks.current.length) {
        const callback = initCallbacks.current.shift();
        if (typeof callback === 'function') {
          callback();
        }
      }
      return true;
    }
    return false;
  }, []);

  const initAutocomplete = useCallback((callback) => {
    if (window.google?.maps?.places) {
      if (searchInputRef.current) {
        try {
          autocompleteRef.current = new window.google.maps.places.Autocomplete(
            searchInputRef.current,
            {
              componentRestrictions: { country: 'gb' },
              fields: ['address_components', 'geometry', 'formatted_address', 'place_id'],
              types: ['address'],
            }
          );
          if (callback) callback(autocompleteRef.current);
          return autocompleteRef.current;
        } catch (error) {
          console.error('Error initializing autocomplete:', error);
          if (callback) callback(null, error);
          return null;
        }
      }
      if (callback) callback(null, new Error('Search input ref not available'));
      return null;
    } else {
      // Queue the callback if Google Maps isn't ready
      if (callback) initCallbacks.current.push(() => initAutocomplete(callback));
      return null;
    }
  }, []);

  useEffect(() => {
    const loadScript = () => {
      // Check if already loaded
      if (initializeGoogleMaps()) {
        setIsScriptLoaded(true);
        return;
      }

      // Prevent multiple script loads
      if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
        // Script is already loading, just wait for it
        const checkLoaded = setInterval(() => {
          if (window.google?.maps?.places) {
            clearInterval(checkLoaded);
            initializeGoogleMaps();
            setIsScriptLoaded(true);
          }
        }, 100);
        return;
      }

      const isProduction =
        window.location.hostname.includes('awsapprunner.com') ||
        window.location.hostname.includes('dwelz.co.uk');
      const apiKey = isProduction ? 'AIzaSyBU74nJXPGTwsrA_0okArQXq5SCJAGD3Iw' : config.maps.apiKey;
      
      if (!apiKey || apiKey === 'YOUR_API_KEY_HERE') {
        toast({
          title: 'Error',
          description: 'Google Maps API key is missing or invalid',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initMap`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsScriptLoaded(true);
        initializeGoogleMaps();
      };
      
      script.onerror = () => {
        toast({
          title: 'Error',
          description: 'Failed to load Google Maps API',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      };
      
      document.head.appendChild(script);
      
      // Set a timeout to check if the script loaded properly
      const timeout = setTimeout(() => {
        if (!window.google?.maps) {
          toast({
            title: 'Warning',
            description: 'Google Maps is taking longer than expected to load',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }, 5000);
      
      return () => {
        clearTimeout(timeout);
        // Clean up the script if the component unmounts
        if (script.parentNode === document.head) {
          document.head.removeChild(script);
        }
        // Clear any pending callbacks
        initCallbacks.current = [];
      };
    };
    
    loadScript();
    
    // Cleanup function
    return () => {
      // Clean up any intervals
      const intervals = [];
      for (let i = 1; i < 99999; i++) {
        // Find any intervals we might have created
        const interval = window[i];
        if (interval && interval._intervalId) {
          clearInterval(interval._intervalId);
          intervals.push(interval);
        }
      }
      
      // Clean up any event listeners
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      }
    };
  }, [initAutocomplete, toast]);

  return { googleMapsLoaded, searchInputRef, geocoder, autocompleteRef };
};

export default useGoogleMapsLoader;
